import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { readViewportCssDimensions, subscribeViewportChange } from '@renderer/utils/viewportMetrics'

interface ScaleToFitProps {
  screenSize: {
    width: number
    height: number
  }
  children: React.ReactNode
  /** Background of the design canvas (sharp, inner stage). */
  backgroundColor?: string
  /**
   * Optional blurred duplicate of the stage for letterboxing (e.g. welcome: same layout as `children`, scaled + blurred).
   * Prefer this over `blurBackgroundSrc` when the filler should match the composed canvas.
   */
  backdropChildren?: React.ReactNode
  /** Optional full-bleed image behind letterboxing when `backdropChildren` is not set. */
  blurBackgroundSrc?: string
  /** Solid color for letterbox when `blurBackgroundSrc` is not set (falls back to black). */
  blurBackgroundColor?: string
}

/** Small extra scale on object-cover backdrop so blur doesn’t leave a faint gap at viewport edges. */
const LETTERBOX_BACKDROP_BLEED = 1.06

/** The CSS scale() applied by ScaleToFit. Children can counter-scale with `1 / scale`. */
const ScaleContext = createContext<number>(1)
export const useCanvasScale = (): number => useContext(ScaleContext)

function useWindowSize(): { cw: number; ch: number } {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1)
    return subscribeViewportChange(bump)
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- tick triggers recompute
  void tick

  if (typeof window === 'undefined') {
    return { cw: 1080, ch: 1920 }
  }
  const { width: cw, height: ch } = readViewportCssDimensions()
  return { cw, ch }
}

/**
 * Keep a box pinned at scroll offset 0, forever.
 *
 * The design canvas is laid out at its full design size (e.g. 1080×1920) and only
 * *painted* smaller by `transform: scale()` — so on any viewport shorter than the
 * design, the canvas box overflows its `overflow-hidden` shell. `overflow: hidden`
 * still leaves a box **programmatically** scrollable: one `scrollIntoView()` (or a
 * focus) on any descendant — a modal's own jump nav, an autofocused input — scrolls
 * the SHELL as well as the intended scroller, and nothing ever scrolls it back. The
 * whole booth then sits permanently offset: the top chrome (reprint / settings /
 * fullscreen padlock) lands above the window edge and the operator can't reach it
 * without restarting the app. Field report: Kabar Kamu (newspaper-ahbox), 2026-08-18.
 *
 * The shell's real fix is `overflow: clip` (inline, with the `overflow-hidden` class
 * as the fallback for renderers that don't support it): a clipped box is not a scroll
 * container at all, so nothing can scroll it. This guard is the backstop for those
 * renderers, and it also covers the canvas box, which keeps `hidden` so that it goes
 * on establishing a block formatting context for the page inside it.
 */
function usePinnedScroll<T extends HTMLElement>(): React.RefObject<T> {
  const ref = useRef<T>(null)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const pin = (): void => {
      if (node.scrollTop !== 0) node.scrollTop = 0
      if (node.scrollLeft !== 0) node.scrollLeft = 0
    }
    pin()
    node.addEventListener('scroll', pin, { passive: true })
    return (): void => node.removeEventListener('scroll', pin)
  }, [])
  return ref
}

/**
 * Fit the design canvas into the viewport with uniform scale (object-contain).
 * Letterbox: `backdropChildren` (blurred stage duplicate at object-cover scale), else blurred theme image, else solid color.
 */
const ScaleToFit: React.FC<ScaleToFitProps> = ({
  screenSize,
  children,
  backgroundColor = 'white',
  backdropChildren,
  blurBackgroundSrc,
  blurBackgroundColor
}) => {
  const { cw, ch } = useWindowSize()
  const { width: pw, height: ph } = screenSize
  const scale =
    pw > 0 && ph > 0 && cw > 0 && ch > 0 ? Math.min(cw / pw, ch / ph) : 1
  /** Uniform scale so design canvas covers the viewport (for blurred backdrop only; sharp layer stays contain). */
  const backdropCoverScale =
    pw > 0 && ph > 0 && cw > 0 && ch > 0 ? Math.max(cw / pw, ch / ph) : 1

  const letterboxFill = blurBackgroundColor ?? backgroundColor ?? '#000000'

  const shellRef = usePinnedScroll<HTMLDivElement>()
  const canvasRef = usePinnedScroll<HTMLDivElement>()

  return (
    <ScaleContext.Provider value={scale}>
      <div
        ref={shellRef}
        className="relative flex h-full min-h-0 w-full min-w-0 flex-1 items-center justify-center overflow-hidden"
        style={{ overflow: 'clip' }}
      >
        <div
          className="absolute inset-0 z-0 overflow-hidden"
          style={{ backgroundColor: letterboxFill }}
          aria-hidden
        >
          {backdropChildren ? (
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 select-none"
              style={{
                width: `${pw}px`,
                height: `${ph}px`,
                transform: `translate(-50%, -50%) scale(${backdropCoverScale * LETTERBOX_BACKDROP_BLEED})`,
                transformOrigin: 'center center',
                filter: 'blur(32px)',
                willChange: 'transform, filter'
              }}
            >
              {backdropChildren}
            </div>
          ) : blurBackgroundSrc ? (
            <img
              src={blurBackgroundSrc}
              alt=""
              className="absolute left-1/2 top-1/2 min-h-[120%] min-w-[120%] max-w-none object-cover"
              style={{
                transform: 'translate(-50%, -50%) scale(1.12)',
                filter: 'blur(32px)'
              }}
            />
          ) : null}
        </div>
        <div
          ref={canvasRef}
          className="relative z-10 shrink-0 overflow-hidden"
          style={{
            width: `${pw}px`,
            height: `${ph}px`,
            backgroundColor,
            transform: `scale(${scale})`,
            transformOrigin: 'center center'
          }}
        >
          {children}
        </div>
      </div>
    </ScaleContext.Provider>
  )
}

export default ScaleToFit
